import { useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { Alert, Button, Link, Stack, TextField, Typography } from "@mui/material";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import ReplayIcon from "@mui/icons-material/Replay";
import { useTranslation } from "react-i18next";
import AuthPageShell from "../components/auth/AuthPageShell";
import { resendVerification } from "../api/auth";

const ResendVerificationPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [identifier, setIdentifier] = useState(
    searchParams.get("identifier") || searchParams.get("email") || ""
  );
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [verificationUrl, setVerificationUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const returnTo = searchParams.get("returnTo") || "/dashboard";

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage("");
    setError("");
    setVerificationUrl("");
    setLoading(true);

    try {
      const data = await resendVerification(identifier, returnTo);
      setMessage(data.message || t("authResend.success"));
      setVerificationUrl(data.verificationUrl || "");
    } catch (err) {
      if (err.response?.data?.code === "RATE_LIMITED") {
        setError(t("authResend.rateLimited"));
      } else {
        setError(err.response?.data?.error || t("authResend.error"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthPageShell
      compact
      title={t("authResend.title")}
      subtitle={t("authResend.subtitle")}
    >
      <Stack component="form" spacing={2} onSubmit={handleSubmit}>
        <Typography color="text.secondary">
          {t("authResend.copy")}
        </Typography>

        <TextField
          label={t("authResend.identifier")}
          value={identifier}
          onChange={(event) => setIdentifier(event.target.value)}
          autoComplete="email"
          required
          fullWidth
        />

        {message && (
          <Stack spacing={1.25}>
            <Alert severity="success">{message}</Alert>
            <Button
              component="a"
              href="mailto:"
              variant="outlined"
              startIcon={<MarkEmailReadIcon />}
              fullWidth
            >
              {t("authVerify.openMailbox")}
            </Button>
          </Stack>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {verificationUrl && (
          <Alert severity="info">
            <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
              {t("authVerify.devVerifyLink")}{" "}
              <Link
                component={RouterLink}
                to={
                  new URL(verificationUrl).pathname +
                  new URL(verificationUrl).search
                }
              >
                {verificationUrl}
              </Link>
            </Typography>
          </Alert>
        )}

        <Button
          type="submit"
          variant="contained"
          startIcon={<ReplayIcon />}
          disabled={loading}
          fullWidth
        >
          {loading ? t("authResend.sending") : t("authResend.submit")}
        </Button>

        <Link
          component={RouterLink}
          to={`/login?returnTo=${encodeURIComponent(returnTo)}`}
          underline="hover"
        >
          {t("authResend.backToLogin")}
        </Link>
      </Stack>
    </AuthPageShell>
  );
};

export default ResendVerificationPage;
